import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { googleSignIn, formatApiError, setToken as setStoredToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

// Module-level guard so an exchange survives StrictMode double-effects and
// any React unmount/remount that happens before the navigate-away takes effect.
let inFlightSessionId = null;
let consumedSessionId = null;

// Handles return from https://auth.emergentagent.com — the redirect lands at our root
// with `#session_id=<id>`. We exchange it for our own JWT, then navigate to the game.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const { loginUser } = useAuth();
  const navigate = useNavigate();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sid = m ? decodeURIComponent(m[1]) : null;
    if (!sid) {
      navigate("/login", { replace: true });
      return;
    }
    // Module-level dedupe — handles StrictMode + remount + accidental double-renders.
    if (consumedSessionId === sid || inFlightSessionId === sid) {
      return;
    }
    inFlightSessionId = sid;

    // Drop any stale JWT from a previous account before exchanging the new session.
    // Prevents the axios interceptor from attaching a stale Bearer header to the
    // /api/auth/google request (harmless on the backend, but eliminates one variable).
    setStoredToken(null);

    (async () => {
      try {
        const { token, user } = await googleSignIn(sid);
        consumedSessionId = sid;
        inFlightSessionId = null;
        loginUser(token, user);
        toast.success(`Welcome, ${user.name || user.email}`);
        // Use react-router navigation so AppRouter's `useLocation()` sees the
        // new hash-less location and re-renders away from AuthCallback.
        navigate("/", { replace: true });
      } catch (e) {
        inFlightSessionId = null;
        // Surface the underlying error to the browser console for diagnostics.
        // (Toast stays user-friendly.)
        // eslint-disable-next-line no-console
        console.error("[AuthCallback] Google sign-in failed:", e);
        const status = e?.response?.status;
        const apiDetail = formatApiError(e?.response?.data?.detail);
        let msg = apiDetail;
        if (!apiDetail || apiDetail === "Something went wrong. Please try again.") {
          msg = status
            ? `Google sign-in failed (HTTP ${status}). Try again.`
            : "Google sign-in failed — check your connection and try again.";
        }
        toast.error(msg);
        navigate("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen w-full bg-[#050505] text-white flex items-center justify-center">
      <div className="mono text-[12px] uppercase tracking-widest text-zinc-400">
        Authenticating…
      </div>
    </div>
  );
}
